import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { MoreHorizontal, Search, Filter, CheckSquare, Trash2, CheckCircle, XCircle, X } from "lucide-react";

const statusClass = {
  confirmed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  cancelled: "bg-red-500/10 text-red-400 border-red-500/20",
};

export default function AppointmentsPage() {
  const { t } = useTranslation();
  const [appointments, setAppointments] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editDialog, setEditDialog] = useState(false);
  const [editApt, setEditApt] = useState(null);
  const [editForm, setEditForm] = useState({ date: "", start_time: "", status: "", notes: "" });
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    api.get("/appointments", { params }).then((r) => setAppointments(r.data)).catch(() => toast.error(t("appointments_load_error")));
  }, [statusFilter]);

  useEffect(() => { load(); setSelected(new Set()); }, [load]);

  const filtered = appointments.filter((a) =>
    a.customer_name.toLowerCase().includes(search.toLowerCase()) ||
    a.service_name.toLowerCase().includes(search.toLowerCase()) ||
    a.customer_email.toLowerCase().includes(search.toLowerCase())
  );

  const allSelected = filtered.length > 0 && filtered.every((a) => selected.has(a.id));
  const someSelected = selected.size > 0;

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(filtered.map((a) => a.id)));
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/appointments/${id}/status`, { status });
      toast.success(t("appointments_status_updated", { status }));
      load();
    } catch { toast.error(t("appointments_status_error")); }
  };

  const openEdit = (apt) => {
    setEditApt(apt);
    setEditForm({ date: apt.date, start_time: apt.start_time, status: apt.status, notes: apt.notes || "" });
    setEditDialog(true);
  };

  const saveEdit = async () => {
    try {
      await api.put(`/appointments/${editApt.id}`, editForm);
      toast.success(t("appointments_updated"));
      setEditDialog(false);
      load();
    } catch { toast.error(t("appointments_update_error")); }
  };

  const deleteApt = async (id) => {
    try {
      await api.delete(`/appointments/${id}`);
      toast.success(t("appointments_deleted"));
      load();
    } catch { toast.error(t("appointments_delete_error")); }
  };

  const bulkAction = async (action) => {
    if (selected.size === 0) return;
    setBulkLoading(true);
    const ids = [...selected];
    try {
      await Promise.all(ids.map((id) => {
        if (action === "delete") return api.delete(`/appointments/${id}`);
        return api.patch(`/appointments/${id}/status`, { status: action });
      }));
      toast.success(`${ids.length} appointment(s) ${action === "delete" ? "deleted" : action + "ed"}`);
      setSelected(new Set());
      load();
    } catch {
      toast.error("Some actions failed");
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div data-testid="appointments-page" className="space-y-6 animate-fade-in">
      <h1 className="text-2xl font-bold tracking-tight font-heading text-white">{t("appointments_title")}</h1>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("appointments_search")}
            className="pl-9 bg-slate-900/50 border-slate-700" data-testid="appointments-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-slate-900/50 border-slate-700" data-testid="status-filter">
            <Filter size={14} className="mr-2 text-slate-400" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all">{t("appointments_filter_all")}</SelectItem>
            <SelectItem value="confirmed">{t("appointments_filter_confirmed")}</SelectItem>
            <SelectItem value="pending">{t("appointments_filter_pending")}</SelectItem>
            <SelectItem value="cancelled">{t("appointments_filter_cancelled")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk action bar */}
      {someSelected && (
        <div className="flex items-center gap-3 bg-indigo-500/10 border border-indigo-500/30 rounded-xl px-4 py-2.5">
          <span className="text-sm text-indigo-300 font-medium flex-1">
            <CheckSquare size={15} className="inline mr-1.5 mb-0.5" />
            {selected.size} selected
          </span>
          <Button size="sm" disabled={bulkLoading} onClick={() => bulkAction("confirmed")}
            className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30 h-7 px-3 text-xs">
            <CheckCircle size={13} className="mr-1" /> Confirm
          </Button>
          <Button size="sm" disabled={bulkLoading} onClick={() => bulkAction("cancelled")}
            className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30 h-7 px-3 text-xs">
            <XCircle size={13} className="mr-1" /> Cancel
          </Button>
          <Button size="sm" disabled={bulkLoading} onClick={() => bulkAction("delete")}
            className="bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/30 h-7 px-3 text-xs">
            <Trash2 size={13} className="mr-1" /> Delete
          </Button>
          <button onClick={() => setSelected(new Set())} className="text-slate-500 hover:text-slate-300 ml-1">
            <X size={15} />
          </button>
        </div>
      )}

      <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-500">{t("appointments_none")}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="w-10 pl-4">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 accent-indigo-500 cursor-pointer"
                  />
                </TableHead>
                <TableHead className="text-slate-400">{t("appointments_customer")}</TableHead>
                <TableHead className="text-slate-400">{t("appointments_service")}</TableHead>
                <TableHead className="text-slate-400">{t("appointments_staff")}</TableHead>
                <TableHead className="text-slate-400">{t("date")}</TableHead>
                <TableHead className="text-slate-400">{t("time")}</TableHead>
                <TableHead className="text-slate-400">{t("status")}</TableHead>
                <TableHead className="text-slate-400 w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((apt) => (
                <TableRow
                  key={apt.id}
                  className={`border-slate-800/50 hover:bg-slate-800/30 transition-colors ${selected.has(apt.id) ? "bg-indigo-500/5" : ""}`}
                  data-testid={`appointment-row-${apt.id}`}
                >
                  <TableCell className="pl-4">
                    <input
                      type="checkbox"
                      checked={selected.has(apt.id)}
                      onChange={() => toggleOne(apt.id)}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-800 accent-indigo-500 cursor-pointer"
                    />
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium text-white">{apt.customer_name}</div>
                    <div className="text-xs text-slate-500">{apt.customer_email}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: apt.service_color || "#6366f1" }} />
                      <span className="text-sm text-slate-300">{apt.service_name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-300">{apt.staff_name || <span className="text-slate-600">—</span>}</TableCell>
                  <TableCell className="text-sm text-slate-300">{format(parseISO(apt.date), "MMM d, yyyy")}</TableCell>
                  <TableCell className="text-sm font-mono text-slate-300">{apt.start_time} - {apt.end_time}</TableCell>
                  <TableCell>
                    <Badge className={`border text-[11px] ${statusClass[apt.status] || ""}`}>{apt.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400" data-testid={`apt-actions-${apt.id}`}>
                          <MoreHorizontal size={14} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                        <DropdownMenuItem onClick={() => openEdit(apt)} className="text-slate-300 focus:text-white">{t("appointments_edit")}</DropdownMenuItem>
                        {apt.status !== "confirmed" && <DropdownMenuItem onClick={() => updateStatus(apt.id, "confirmed")} className="text-emerald-400 focus:text-emerald-300">{t("appointments_confirm")}</DropdownMenuItem>}
                        {apt.status !== "cancelled" && <DropdownMenuItem onClick={() => updateStatus(apt.id, "cancelled")} className="text-red-400 focus:text-red-300">{t("appointments_cancel")}</DropdownMenuItem>}
                        <DropdownMenuItem onClick={() => deleteApt(apt.id)} className="text-red-400 focus:text-red-300">{t("appointments_delete")}</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("appointments_edit_title")}</DialogTitle>
            <DialogDescription className="text-slate-400">{t("appointments_edit_sub")}</DialogDescription>
          </DialogHeader>
          {editApt && (
            <div className="space-y-4 py-2">
              <div className="bg-slate-800/50 rounded-lg p-3 text-sm">
                <span className="text-slate-400">Customer:</span> <span className="text-white ml-1">{editApt.customer_name}</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm">{t("date")}</Label>
                  <Input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                    className="bg-slate-800 border-slate-700" data-testid="edit-apt-date" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm">{t("time")}</Label>
                  <Input type="time" value={editForm.start_time} onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })}
                    className="bg-slate-800 border-slate-700" data-testid="edit-apt-time" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">{t("status")}</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="edit-apt-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="confirmed">{t("appointments_filter_confirmed")}</SelectItem>
                    <SelectItem value="pending">{t("appointments_filter_pending")}</SelectItem>
                    <SelectItem value="cancelled">{t("appointments_filter_cancelled")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">{t("notes")}</Label>
                <Input value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder={t("appointments_notes_placeholder")} className="bg-slate-800 border-slate-700" data-testid="edit-apt-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialog(false)} className="text-slate-400">{t("cancel")}</Button>
            <Button onClick={saveEdit} className="bg-indigo-500 hover:bg-indigo-600" data-testid="save-apt-button">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
